import { useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useSearchParams } from 'react-router';
import io from 'socket.io-client';

import GET_RETURNED_ORDER from '../gql/get-returned-order.gql';
import digitalData from '../utils/digitalData';
import { useGetDigitalDataFlagValue } from '../utils/digital-data-flag';
import getSocketClient from '../utils/socket-client';
import { getParams } from '../utils/params';

/**
 * Custom hook for handling order loading, polling, and socket client logic
 * 
 * @returns {Object} The hook's return object
 * @returns {Object} returns.returnedOrder - The returned order data
 * @returns {Object} returns.confirmationDetails - Confirmation details for the order
 * @returns {boolean} returns.loadingFailure - Whether loading has failed
 * @returns {string} returns.labelOrderId - The label order ID from URL params
 * @returns {number} returns.queryCount - Number of queries made
 * @returns {boolean} returns.isReadyToDisplay - Whether the order is ready to display
 */
export default function useGetReturnedOrder() {
  const [queryCount, setQueryCount] = useState(0);
  const [loadingFailure, setLoadingFailure] = useState(false);
  const [searchParams] = useSearchParams();
  const timeout = useRef();
  const loadingStart = useRef();
  const socketClient = useRef();

  const useSocketClient = !!useGetDigitalDataFlagValue('ful-dre-2-0-socket-client', false);
  const labelOrderId = searchParams.get('label');

  // Order Detail (label generation)
  const [fetchReturnedOrder, { data: fetchedReturnedOrder }] = useLazyQuery(GET_RETURNED_ORDER, {
    fetchPolicy: 'no-cache',
  });

  const returnedOrder = useMemo(() => {
    if (!fetchedReturnedOrder?.returnedOrder) {
      return {};
    }
    return fetchedReturnedOrder.returnedOrder;
  }, [fetchedReturnedOrder]);

  const confirmationDetails = useMemo(() => {
    if (!returnedOrder?.confirmationDetails) {
      return {};
    }
    return returnedOrder.confirmationDetails;
  }, [returnedOrder]);

  const { isReadyToDisplay, assetAvailable } = confirmationDetails;

  // Polling and socket logic
  useEffect(() => {
    const cleanUpTimer = () => {
      clearTimeout(timeout.current);
      timeout.current = null;
    };

    if (!loadingStart.current) {
      loadingStart.current = Date.now();
    }

    if (isReadyToDisplay) {
      if (!assetAvailable) {
        setLoadingFailure(true);
      }
      return cleanUpTimer;
    }

    if (useSocketClient && !socketClient.current) {
      const { orderEmail } = getParams(window);
      socketClient.current = getSocketClient(io, window, console);
      socketClient.current.orderReady(labelOrderId, orderEmail)
        .then((orderReady) => {
          if (orderReady) {
            fetchReturnedOrder({
              variables: {
                orderId: labelOrderId,
                count: queryCount,
              },
            });
          } else {
            setLoadingFailure(true);
          }
        })
        .catch(() => {
          fetchReturnedOrder({
            variables: {
              orderId: labelOrderId,
              count: queryCount,
            },
          });
        });
      return cleanUpTimer;
    }

    // We only want to poll the server for a minute. If we haven't received a
    // label yet, display an error and stop polling.
    if (Math.abs(loadingStart.current - (Date.now())) >= 60000) {
      setLoadingFailure(true);
      return cleanUpTimer;
    }

    const delay = digitalData(global?.window, 'ful-dre-2-0-post-submit-delay', 10);

    timeout.current = setTimeout(() => {
      fetchReturnedOrder({
        variables: {
          orderId: labelOrderId,
          count: queryCount,
        },
      });
      setQueryCount(queryCount + 1);
    }, delay * 1000);

    return cleanUpTimer;
  }, [
    returnedOrder,
    queryCount,
    loadingStart,
    labelOrderId,
    useSocketClient,
    isReadyToDisplay,
    fetchReturnedOrder,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, []);

  return {
    returnedOrder,
    confirmationDetails,
    loadingFailure,
    labelOrderId,
    queryCount,
    isReadyToDisplay,
  };
}; 